import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  Children,
  isValidElement,
  useCallback,
  useState,
  type ReactNode,
  type HTMLAttributes,
  type CSSProperties,
} from 'react';
import type { PagiflowInstance, PagiflowOptions } from './types';

// @ts-ignore
import PagiflowCore from 'pagiflow';

type PagiflowCtor = (
  selector: string | HTMLElement,
  opts?: PagiflowOptions
) => PagiflowInstance;

// ─── Pagiflow loader (same as hook, kept local to avoid circular) ─────────────
function getPagiflow(): PagiflowCtor | null {
  if (typeof window !== 'undefined' && (window as any).Pagiflow) {
    return (window as any).Pagiflow as PagiflowCtor;
  }
  return ((PagiflowCore as any).default ?? PagiflowCore) as PagiflowCtor;
}

function initPagiflowFromElement(
  ctor: PagiflowCtor,
  el: HTMLElement,
  opts: PagiflowOptions
): PagiflowInstance | null {
  const attrValue = `pf-${Math.random().toString(36).slice(2)}`;
  const selector = `[data-pagiflow-react-id="${attrValue}"]`;
  el.setAttribute('data-pagiflow-react-id', attrValue);

  const bySelector = ctor(selector, opts) as PagiflowInstance | null;
  if (!bySelector) {
    el.removeAttribute('data-pagiflow-react-id');
    return null;
  }
  return bySelector;
}

// ─── Slot Component for asChild pattern ──────────────────────────────────────────
const Slot = forwardRef<HTMLElement, HTMLAttributes<HTMLElement> & { children?: ReactNode }>(
  ({ children, ...props }, ref) => {
    if (isValidElement(children)) {
      const clonedProps: any = {
        ...props,
        ...children.props,
        style: {
          ...props.style,
          ...children.props.style,
        },
        className: [props.className, children.props.className].filter(Boolean).join(' ') || undefined,
      };

      const childRef = (children as any).ref;
      if (ref || childRef) {
        clonedProps.ref = (node: any) => {
          if (typeof childRef === 'function') childRef(node);
          else if (childRef && 'current' in childRef) childRef.current = node;

          if (typeof ref === 'function') ref(node);
          else if (ref && 'current' in ref) (ref as any).current = node;
        };
      }

      return React.cloneElement(children as React.ReactElement<any>, clonedProps);
    }
    return null;
  }
);
Slot.displayName = 'Slot';

// ─── PagiflowSlide ────────────────────────────────────────────────────────────
export interface PagiflowSlideProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  /**
   * If true, changes the element rendered to the direct child element,
   * merging their props and behavior.
   */
  asChild?: boolean;
}

/**
 * Wrapper for individual slides. Each direct child of `<Pagiflow>` becomes
 * a slide. By default (`asChild: true`), this acts as a Slot and merges
 * its props into the child element to avoid an extra wrapper `<div>`.
 * Pass `asChild={false}` to explicitly render a wrapper `<div>`.
 */
export const PagiflowSlide = forwardRef<HTMLElement, PagiflowSlideProps>(
  ({ children, className, style, asChild = true, ...rest }, ref) => {
    const Comp = asChild && isValidElement(children) ? Slot : 'div';
    return (
      <Comp ref={ref as any} className={className} style={style} {...rest}>
        {children}
      </Comp>
    );
  }
);
PagiflowSlide.displayName = 'PagiflowSlide';

// ─── Pagiflow Imperative Handle ───────────────────────────────────────────────
export interface PagiflowHandle {
  instance: PagiflowInstance | null;
  next(): void;
  prev(): void;
  goTo(index: number, opts?: { silent?: boolean; instant?: boolean }): void;
  play(): void;
  pause(): void;
  resume(): void;
  togglePlayPause(): void;
  setOptions(opts: Partial<PagiflowOptions>, rebuild?: boolean): void;
  reInit(opts?: Partial<PagiflowOptions>): void;
}

// ─── Pagiflow Props ───────────────────────────────────────────────────────────
export interface PagiflowProps extends PagiflowOptions {
  /** Slide content — accepts any React children */
  children?: ReactNode;
  /** CSS class applied to the track element */
  className?: string;
  /** Inline style for the track element */
  style?: CSSProperties;
  /** Called when the active slide changes (real index, 0-based) */
  onSlideChange?: (index: number) => void;
  /**
   * Render prop for custom navigation.
   * Receives `{ next, prev, currentIndex, instance }`.
   */
  renderNav?: (props: {
    next: () => void;
    prev: () => void;
    currentIndex: number;
    instance: PagiflowInstance | null;
  }) => ReactNode;
  /**
   * Render prop for custom pagination.
   * Receives `{ goTo, currentIndex, total, instance }`.
   */
  renderPagination?: (props: {
    goTo: (i: number) => void;
    currentIndex: number;
    total: number;
    instance: PagiflowInstance | null;
  }) => ReactNode;
  /** ID applied to the underlying track element */
  id?: string;
}

// ─── <Pagiflow> component ─────────────────────────────────────────────────────
/**
 * Drop-in React component wrapping the Pagiflow core.
 *
 * @example
 * <Pagiflow loop nav paginate onSlideChange={setIdx}>
 *   <PagiflowSlide><img src="..." /></PagiflowSlide>
 *   <PagiflowSlide><img src="..." /></PagiflowSlide>
 * </Pagiflow>
 */
export const Pagiflow = forwardRef<PagiflowHandle, PagiflowProps>(
  (
    {
      children,
      className,
      style,
      onSlideChange,
      renderNav,
      renderPagination,
      id,
      // Extract all PagiflowOptions — anything left goes to the track div
      direction,
      fade,
      grid,
      gridColumns,
      gridFill,
      animate,
      itemsPerSlide,
      slidesToScroll,
      gap,
      height,
      startIndex,
      loop,
      speed,
      swipeThreshold,
      nav,
      navDisabledEnd,
      navigation,
      prevIcon,
      nextIcon,
      prevPosition,
      nextPosition,
      thumbnails,
      autoplay,
      autoplayDelay,
      pauseOnHover,
      paginate,
      paginationPosition,
      paginationType,
      autoplayProgress,
      freeMode,
      freeModeMomentum,
      draggable,
      on,
      autoScroll,
      autoScrollSpeed,
      autoScrollDirection,
      lazyLoad,
      keyboard,
      centerMode,
      centerPadding,
      rtl,
      sync,
      responsive,
      ...rest
    },
    ref
  ) => {
    const trackRef = useRef<HTMLDivElement | null>(null);
    const instanceRef = useRef<PagiflowInstance | null>(null);
    const onSlideChangeRef = useRef(onSlideChange);
    const currentIndexRef = useRef(startIndex ?? 0);
    const [, setUiTick] = useState(0);

    // Keep callback ref fresh
    onSlideChangeRef.current = onSlideChange;

    // Collect all Pagiflow options into one object
    const buildOptions = useCallback((): PagiflowOptions => {
      const opts: PagiflowOptions = {};
      if (direction !== undefined) opts.direction = direction;
      if (fade !== undefined) opts.fade = fade;
      if (grid !== undefined) opts.grid = grid;
      if (gridColumns !== undefined) opts.gridColumns = gridColumns;
      if (gridFill !== undefined) opts.gridFill = gridFill;
      if (animate !== undefined) opts.animate = animate;
      if (itemsPerSlide !== undefined) opts.itemsPerSlide = itemsPerSlide;
      if (slidesToScroll !== undefined) opts.slidesToScroll = slidesToScroll;
      if (gap !== undefined) opts.gap = gap;
      if (height !== undefined) opts.height = height;
      if (startIndex !== undefined) opts.startIndex = startIndex;
      if (loop !== undefined) opts.loop = loop;
      if (speed !== undefined) opts.speed = speed;
      if (swipeThreshold !== undefined) opts.swipeThreshold = swipeThreshold;
      if (nav !== undefined) opts.nav = nav;
      if (navDisabledEnd !== undefined) opts.navDisabledEnd = navDisabledEnd;
      if (navigation !== undefined) opts.navigation = navigation;
      if (prevIcon !== undefined) opts.prevIcon = prevIcon;
      if (nextIcon !== undefined) opts.nextIcon = nextIcon;
      if (prevPosition !== undefined) opts.prevPosition = prevPosition;
      if (nextPosition !== undefined) opts.nextPosition = nextPosition;
      if (thumbnails !== undefined) opts.thumbnails = thumbnails;
      if (autoplay !== undefined) opts.autoplay = autoplay;
      if (autoplayDelay !== undefined) opts.autoplayDelay = autoplayDelay;
      if (pauseOnHover !== undefined) opts.pauseOnHover = pauseOnHover;
      if (paginate !== undefined) opts.paginate = paginate;
      if (paginationPosition !== undefined) opts.paginationPosition = paginationPosition;
      if (paginationType !== undefined) opts.paginationType = paginationType;
      if (autoplayProgress !== undefined) opts.autoplayProgress = autoplayProgress;
      if (freeMode !== undefined) opts.freeMode = freeMode;
      if (freeModeMomentum !== undefined) opts.freeModeMomentum = freeModeMomentum;
      if (draggable !== undefined) opts.draggable = draggable;
      if (on !== undefined) opts.on = on;
      if (autoScroll !== undefined) opts.autoScroll = autoScroll;
      if (autoScrollSpeed !== undefined) opts.autoScrollSpeed = autoScrollSpeed;
      if (autoScrollDirection !== undefined) opts.autoScrollDirection = autoScrollDirection;
      if (lazyLoad !== undefined) opts.lazyLoad = lazyLoad;
      if (keyboard !== undefined) opts.keyboard = keyboard;
      if (centerMode !== undefined) opts.centerMode = centerMode;
      if (centerPadding !== undefined) opts.centerPadding = centerPadding;
      if (rtl !== undefined) opts.rtl = rtl;
      if (sync !== undefined) opts.sync = sync;
      if (responsive !== undefined) opts.responsive = responsive;
      return opts;
    }, [
      // Only recreate buildOptions when the serialized options change
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify({
        direction, fade, grid, gridColumns, gridFill, animate, itemsPerSlide,
        slidesToScroll, gap, height, startIndex, loop, speed, swipeThreshold,
        nav, navDisabledEnd, navigation, prevIcon, nextIcon, prevPosition,
        nextPosition, thumbnails, autoplay, autoplayDelay, pauseOnHover,
        paginate, paginationPosition, paginationType, autoScroll, autoScrollSpeed,
        autoplayProgress, freeMode, freeModeMomentum, draggable, on,
        autoScrollDirection, lazyLoad, keyboard, centerMode, centerPadding,
        rtl, sync, responsive,
      })
    ]);

    // ─── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => {
      const el = trackRef.current;
      if (!el) return;

      const existing = (el as any).pagiflowInstance as PagiflowInstance | null | undefined;
      if (existing && !existing.destroyed) return;

      // Save complete pristine React DOM state
      const originalChildren = Array.from(el.children);
      const originalClasses = el.className;
      const originalStyle = el.getAttribute('style');

      const PagiflowCtor = getPagiflow();
      if (!PagiflowCtor) {
        console.error(
          '[Pagiflow] Core not found. Install pagiflow or load it via CDN.'
        );
        return;
      }

      const inst = initPagiflowFromElement(PagiflowCtor, el, buildOptions());
      if (!inst) return;
      (el as any).pagiflowInstance = inst;

      const syncLayout = () => {
        const api = inst as PagiflowInstance & { update?: (syncTarget?: unknown) => void };
        api.update?.();
      };

      // Ensure correct sizing after React paints and fonts/layout settle.
      const raf1 = window.requestAnimationFrame(() => syncLayout());
      const raf2 = window.requestAnimationFrame(() => syncLayout());
      const timeoutId = window.setTimeout(() => syncLayout(), 80);
      window.addEventListener('load', syncLayout, { once: true });

      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => syncLayout());
        resizeObserver.observe(el);
      }

      inst.onSlideChange((idx) => {
        currentIndexRef.current = idx;
        onSlideChangeRef.current?.(idx);
        if (renderNav || renderPagination) {
          setUiTick((n) => n + 1);
        }
      });

      instanceRef.current = inst;
      if (renderNav || renderPagination) {
        setUiTick((n) => n + 1);
      }

      return () => {
        window.cancelAnimationFrame(raf1);
        window.cancelAnimationFrame(raf2);
        window.clearTimeout(timeoutId);
        window.removeEventListener('load', syncLayout);
        resizeObserver?.disconnect();
        if (instanceRef.current && !instanceRef.current.destroyed) {
          try { instanceRef.current.destroy(); } catch { /* ignore */ }
        }
        delete (el as any).pagiflowInstance;
        instanceRef.current = null;

        // Fully restore DOM to pristine React state
        el.innerHTML = '';
        originalChildren.forEach(child => el.appendChild(child));
        el.className = originalClasses;
        if (originalStyle !== null) {
          el.setAttribute('style', originalStyle);
        } else {
          el.removeAttribute('style');
        }
        el.removeAttribute('data-pagiflow-react-id');
        delete el.dataset.pagiflowDestroyed;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep core options in sync with prop changes after initial mount.
    useEffect(() => {
      if (!instanceRef.current || instanceRef.current.destroyed) return;
      instanceRef.current.setOptions(buildOptions(), true);
    }, [buildOptions]);

    // ─── Imperative API ────────────────────────────────────────────────────────
    useImperativeHandle(ref, () => ({
      get instance() { return instanceRef.current; },
      next: () => instanceRef.current?.next(),
      prev: () => instanceRef.current?.prev(),
      goTo: (i, o) => instanceRef.current?.goTo(i, o),
      play: () => instanceRef.current?.play(),
      pause: () => instanceRef.current?.pause(),
      resume: () => instanceRef.current?.resume(),
      togglePlayPause: () => instanceRef.current?.togglePlayPause(),
      setOptions: (o, r) => instanceRef.current?.setOptions(o, r),
      reInit: (o) => instanceRef.current?.reInit(o),
    }), []);

    // ─── Helpers ───────────────────────────────────────────────────────────────
    const next = useCallback(() => instanceRef.current?.next(), []);
    const prev = useCallback(() => instanceRef.current?.prev(), []);
    const goTo = useCallback(
      (i: number, o?: { silent?: boolean; instant?: boolean }) =>
        instanceRef.current?.goTo(i, o),
      []
    );

    // Count real slides (ignore non-element children)
    const slideCount = Children.count(
      Children.toArray(children).filter(isValidElement)
    );

    return (
      <>
        <div
          ref={trackRef}
          id={id}
          className={['pagiflow', className].filter(Boolean).join(' ')}
          style={style}
          {...rest}
        >
          {children}
        </div>

        {/* Custom nav render prop */}
        {renderNav &&
          renderNav({
            next,
            prev,
            currentIndex: currentIndexRef.current,
            instance: instanceRef.current,
          })}

        {/* Custom pagination render prop */}
        {renderPagination &&
          renderPagination({
            goTo: (i) => goTo(i),
            currentIndex: currentIndexRef.current,
            total: slideCount,
            instance: instanceRef.current,
          })}
      </>
    );
  }
);
Pagiflow.displayName = 'Pagiflow';
